import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import { CHECKOUT_FIELDS } from "@/shared/gift-cards.constants";

import { createOrder } from "@/utils/afterpay-checkout";
import { convertPriceToNumber } from "@/utils/strings";

import { localeCurrencyCodeMap } from "@/translations/config";

import EVENTS from "@/analytics/events";
import { trackPageLoad } from "@/analytics/tracking";

import { useErrorMessage } from "@/context/ErrorMessageContext";
import { useCurrentLocale } from "@/context/LanguageContext";

import Accordion from "@/components/accordion/accordion";
import ActionButton from "@/components/buttons/action-button";
import { KINDS, SIZES } from "@/components/buttons/buttons.constants";

import {
    Consumer,
    GiftCardCheckoutData,
    GiftCardProps,
    Recipient,
} from "@/interfaces/gift-cards/gift-card-interface";

import { LoaderOverlay } from "../loaders/loader-overlay";
import LearnMore from "./learn-more";
import styles from "./order-confirmation.module.scss";

interface Props {
    giftCard: GiftCardProps;
    isGift: boolean;
    skipConfirmed: boolean;
    skipLogin: boolean;
    selectedAmount: string;
    isPurchaseFlowComplete: boolean;
    consumer: Consumer;
    recipient: Recipient;
    confirmOrder: boolean;
    setConfirmOrder: (confirmOrderStatus: boolean) => void;
    setOrderId: (orderId: number) => void;
    setPurchaseFlowComplete: (isComplete: boolean) => void;
}

const OrderConfirmation: React.FC<Props> = ({
    giftCard,
    isGift,
    skipConfirmed,
    skipLogin,
    selectedAmount,
    consumer,
    recipient,
    confirmOrder,
    setConfirmOrder,
    setOrderId,
    isPurchaseFlowComplete,
    setPurchaseFlowComplete,
}) => {
    const { firstname, email } = consumer;
    const { t } = useTranslation();
    const { createErrorMessage } = useErrorMessage();
    const locale = useCurrentLocale();
    const router = useRouter();
    const [disablePayWithAfterpay, setDisablePayWithAfterpay] = useState<boolean>(false);

    const GIFT_CARD_ERROR_MESSAGES = {
        OUT_OF_STOCK: t("messages.outOfStock"),
        INVALID_RECIPIENT_EMAIL: t("messages.invalidEmail"),
        CANCELLED: t("messages.cancelledOrder"),
    };

    const checkoutData: GiftCardCheckoutData = {
        gift_card_id: Number(giftCard.id),
        amount: skipConfirmed ? Number(selectedAmount) : convertPriceToNumber(selectedAmount),
        currency: localeCurrencyCodeMap[locale],
        sku: giftCard.sku,
        isGift,
        consumer,
        recipient,
    };

    const onPayClicked = async () => {
        trackPageLoad(router.asPath, locale, EVENTS.GIFT_CARD_PAYMENT_START, {
            sku: checkoutData.sku,
            type: isGift ? "other" : "self",
        });

        // There is a brief window when a user can click the "Pay with Afterpay" button a second time
        // and do the checkout process again. Creating two orders.
        setDisablePayWithAfterpay(true);

        try {
            const orderId = await createOrder(checkoutData, locale);
            setOrderId(orderId);
        } catch (err) {
            setPurchaseFlowComplete(true);
            createErrorMessage(GIFT_CARD_ERROR_MESSAGES.CANCELLED);
        }
    };

    useEffect(() => {
        if (skipConfirmed) {
            onPayClicked();
        }
        trackPageLoad(router.asPath, locale, EVENTS.GIFT_CARD_CHECKOUT_SUBMIT, {
            sku: giftCard.sku,
            type: isGift ? "other" : "self",
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const confirmOrderPage = () => {
        return (
            <>
                <div className={styles.orderConfirmationContainer}>
                    <div className={styles.headerContainer}>
                        <div className={styles.backButton}>
                            <span
                                className={styles.heading}
                                onClick={() => setConfirmOrder(!confirmOrder)}
                            >
                                {t("giftCards.orderConfirmation.back")}
                            </span>
                        </div>
                        <div className={styles.header}>
                            <span>{t("giftCards.orderConfirmation.header")}</span>
                        </div>
                    </div>
                    <div className={styles.body}>
                        <div className={styles.informationContainer}>
                            <div className={styles.detail}>
                                <span className={styles.heading}>
                                    {t("giftCards.orderConfirmation.product")}
                                </span>
                                <span className={styles.content}>{giftCard.name}</span>
                            </div>
                            <div className={styles.detail}>
                                <span className={styles.heading}>
                                    {t("giftCards.orderConfirmation.value")}
                                </span>
                                <span className={styles.content}>{selectedAmount}</span>
                            </div>
                            {isGift && (
                                <>
                                    <div className={styles.detail}>
                                        <span className={styles.heading}>
                                            {t("giftCards.orderConfirmation.recipientName")}
                                        </span>
                                        <span className={styles.content}>{recipient.name}</span>
                                    </div>
                                    <div className={styles.detail}>
                                        <span className={styles.heading}>
                                            {t("giftCards.orderConfirmation.recipientEmail")}
                                        </span>
                                        <span className={styles.content}>{recipient.email}</span>
                                    </div>
                                </>
                            )}
                            <div className={styles.detail}>
                                <span className={styles.heading}>
                                    {t("giftCards.orderConfirmation.consumerName")}
                                </span>
                                <span className={styles.content}>{firstname}</span>
                            </div>
                            <div className={styles.detail}>
                                <span className={styles.heading}>
                                    {t("giftCards.orderConfirmation.consumerEmail")}
                                </span>
                                <span className={styles.content}>{email}</span>
                            </div>
                            {isGift && (
                                <div className={styles.detail}>
                                    <span className={styles.heading}>
                                        {t("giftCards.orderConfirmation.message")}
                                    </span>
                                    <p className={styles.content}>{recipient.message}</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.underline} />
                    <div className={styles.orderTotal}>
                        <span className={styles.heading}>
                            {t("giftCards.orderConfirmation.orderTotal")}
                        </span>
                        <span className={styles.heading}>{selectedAmount}</span>
                    </div>
                </div>
                <div className={styles.learnMoreContainer}>
                    <LearnMore price={selectedAmount} />
                </div>
                <div className={styles.checkoutButtonContainer}>
                    {/*
                        Button to initiate the gift card checkout process
                    */}
                    <ActionButton
                        disabled={disablePayWithAfterpay}
                        fluid={true}
                        kind={KINDS.PRIMARY}
                        label={t("giftCards.buttons.checkout")}
                        onClick={onPayClicked}
                        size={SIZES.LARGE}
                    />
                </div>
                <div className={styles.termsContainer}>
                    <Accordion
                        title={t("giftCards.accordions.termsAndConditions")}
                        content={giftCard.terms}
                    />
                </div>
            </>
        );
    };

    /*
     * When using Safari (desktop or iOS) the Afterpay SDK redirects the user to portal.afterpay.com instead of
     * opening a popup and on afterpay.com.
     * Once portal.afterpay.com redirects the user back to the gift card page it will add status and orderToken
     * query parameters in the URL.
     * The state and context are reset, but the status and orderToken query parameters let us resume the checkout.
     * Saving the checkout data to session storage allows us to continue the checkout process after the user is
     * redirected back to the gift card page.
     * With Skip Login, we now leverage window object injection to pass in the token to be used to process the order.
     * Because of this, there isn't a need to set the session storage since it will then trigger multiple copies.
     * This filters out setting up session storage.
     * */
    if (!skipLogin) {
        sessionStorage.setItem(CHECKOUT_FIELDS, JSON.stringify(checkoutData));
    }
    return (
        <>
            {(skipConfirmed || skipLogin) && !isPurchaseFlowComplete ? (
                <LoaderOverlay variant="pulse" />
            ) : (
                confirmOrderPage()
            )}
        </>
    );
};

export default OrderConfirmation;
